// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import DateTimePicker from 'Ui/components/common/date-picker/date-time-picker';
import {Stack, TextField} from '@deere/isg.component-library';
import ValidationInput from 'Ui/components/common/form/validation-input';
import ValidationMultiSelect from 'Ui/components/common/form/validation-multi-select';
import {getAreaOptions, getHoleOptions, validateDate, getLocalTimeFormats, getValidatedTime} from 'Ui/components/manual-data/manual-data-utils';
import {MANUAL_DATA_TIME_FORMAT} from 'Utils/time-utils';
import {capitalizeFirstLetter, replaceTranslationNames} from 'Utils/translation-utils';
import {getPreferredDataTypeForm} from 'Utils/unit-conversion-utils';
import {MIN_NUMERIC_VALUE} from 'Ui/constants/common-constants';
import {FIRMNESS, FIRMNESS_F355_EQUIVALENTS, FIRMNESS_GRAVITIES} from 'Common/constants/data-group-constants';
import {getUOMOverrideUnits} from 'Common/constants/uom-override-units';
import {getDataType} from 'Common/utils/unit-of-measure-utils';

function setupMemos(translations, membership, values) {
    const {
        areaOptions,
        holesOptions,
        firmnessUOM,
        overrideUnits,
        dataType,
        minimumValueMessage
    } = React.useMemo(() => {
        const holesOptions = getHoleOptions(translations);
        const areaOptions = getAreaOptions(translations);
        const overrideUnits = getUOMOverrideUnits(translations);

        const firmnessUOM = values.manualData ? values.manualData[0].data_unit : getPreferredDataTypeForm(
            membership.preferredDataTypes,
            membership.unitOfMeasure,
            [FIRMNESS, FIRMNESS_F355_EQUIVALENTS, FIRMNESS_GRAVITIES]
        );

        const minimumValueMessage = replaceTranslationNames(translations.VALUE_GREATER_THAN_OR_EQUAL, {
            '0': MIN_NUMERIC_VALUE
        });

        const dataType = getDataType(FIRMNESS, firmnessUOM);

        return {
            areaOptions,
            holesOptions,
            firmnessUOM,
            overrideUnits,
            dataType,
            minimumValueMessage
        };
    }, [membership]);

    const uomValue = React.useMemo(() => {
        if (!values.manualData) {
            return '';
        }

        return values.manualData[0].value;
    }, [values.manualData]);

    return {
        areaOptions,
        holesOptions,
        firmnessUOM,
        overrideUnits,
        uomValue,
        dataType,
        minimumValueMessage
    };
}

function FirmnessEntryForm(props) {
    const {
        membership,
        setValid,
        setValues,
        translations,
        values
    } = props;

    function onValuesChange(name, value) {
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }

    const [validatedDate, setValidatedDate] = React.useState();
    const [validatedTime, setValidatedTime] = React.useState();

    const {
        areaOptions,
        holesOptions,
        firmnessUOM,
        overrideUnits,
        uomValue,
        dataType,
        minimumValueMessage
    } = setupMemos(translations, membership, values);

    React.useEffect(() => onValuesChange('holeLocation', areaOptions[0].id), []);

    React.useEffect(() => {
        const validated = validateDate(values.timeSample, setValid);

        setValidatedDate(validated);
        setValidatedTime(getValidatedTime(validated?.toDate()));
    }, [values.timeSample]);

    return (
        <div className='settings-body manual-data-form'>
            <DateTimePicker
                dateProps={{
                    validatedDate
                }}
                onChange={(date) => onValuesChange('timeSample', date?.format(MANUAL_DATA_TIME_FORMAT))}
                timeProps={{
                    validatedTime,
                    timeFormat: getLocalTimeFormats(),
                    timeIntervals: 1,
                    enableVirtualization: true
                }}
                translations
            />
            <Stack
                direction='row'
                spacing={4}
                sx={{
                    width: '100%',
                    '& > *': {
                        flex: 1,
                        minWidth: 0
                    }
                }}
            >
                <ValidationMultiSelect
                    className='mobile-required-multiselect'
                    disabled={false}
                    hideCheckboxes={true}
                    items={holesOptions}
                    label={translations.ONLINK_HOLES}
                    name='hole'
                    onChange={(event) => onValuesChange('hole', event[0])}
                    required={true}
                    selectedIds={[values.hole]}
                    setValid={setValid}
                    single
                    tabIndex={0}
                />
                <ValidationMultiSelect
                    className='mobile-required-multiselect'
                    disabled={false}
                    hideCheckboxes={true}
                    items={areaOptions}
                    label={translations.AREA}
                    name='holeLocation'
                    onChange={(event) => event[0] && onValuesChange('holeLocation', event[0])}
                    required={true}
                    selectedIds={[values.holeLocation]}
                    setValid={setValid}
                    single
                    tabIndex={0}
                /></Stack>
            <ValidationInput
                errors={{
                    rangeUnderflow: minimumValueMessage
                }}
                label={capitalizeFirstLetter(overrideUnits[firmnessUOM]?.title)}
                min={0}
                name={firmnessUOM}
                onChange={(event) => {
                    const value = event.target.value === '' ? '' : parseFloat(event.target.value);

                    onValuesChange('manualData', [{
                        'data_type': dataType,
                        'data_unit': firmnessUOM,
                        value
                    }]);
                }}
                required={true}
                setValid={setValid}
                tabIndex={0}
                type='number'
                value={uomValue}
            />
            <TextField
                label={translations.NOTES}
                multiline
                name='note'
                onChange={(event) => onValuesChange('note', event.target.value)}
                tabIndex={0}
                value={values.note || ''}
            />
        </div>
    );
}

FirmnessEntryForm.propTypes = {
    membership: PropTypes.membership,
    setValid: PropTypes.func,
    setValues: PropTypes.func,
    translations: PropTypes.translations,
    values: PropTypes.object
};

export default FirmnessEntryForm;
